import { useState } from "react";
import { SaveOutlined } from "@ant-design/icons";
import { Button, Form, message, Tabs } from "antd";
import { useTranslation } from "react-i18next";

import { type CheckoutVariant, type PageConfiguration } from "@fitness-app/data-models/entities/CheckoutVariant";
import { getErrorMessage } from "@fitness-app/utils";

import { supabase } from "~/store/initializeStore";

import { ProductCheckoutVariantVisualEditor } from "./ProductCheckoutVariantVisualEditor";
import ProductPageGeneralConfigForm from "./ProductPageGeneralConfigForm";
import ProductPagePricingPlansForm from "./ProductPagePricingPlansForm";

type PricingPlan = NonNullable<PageConfiguration["pricing"]>[number];
type VisualSection = "general" | "timer" | "pricing" | "vipPricing" | "guarantee" | "promoVideo";

// Migrate legacy data structure
const migratePricingPlans = (plans?: PricingPlan[]): PricingPlan[] => {
  if (!plans || plans.length === 0) return [];

  return plans.map((plan) => ({
    ...plan,
    features: plan.features.map(({ isGratis, ...feature }) => ({
      ...feature,
      // Migrate isGratis to isFree
      isFree: feature.isFree ?? isGratis ?? false,
      // Set hideCheck to false if not set (show checkmark by default)
      hideCheck: feature.hideCheck ?? false,
    })),
    bonuses: plan.bonuses.map(({ isGratis, ...bonus }) => ({
      ...bonus,
      // Migrate isGratis to isFree
      isFree: bonus.isFree ?? isGratis ?? false,
      // Set hideCheck to false if not set (show checkmark by default)
      hideCheck: bonus.hideCheck ?? false,
    })),
  }));
};

const getEcommerceOrigin = () => {
  const domain = import.meta.env.VITE_ECOMMERCE_DOMAIN?.trim().replace(/\/+$/, "");
  const isLocalAdmin =
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");

  if (isLocalAdmin) {
    return import.meta.env.VITE_LOCAL_ECOMMERCE_ORIGIN ?? "http://localhost:3001";
  }

  if (!domain) {
    return "";
  }

  if (/^https?:\/\//.test(domain)) {
    return domain;
  }

  return /^(localhost|127\.0\.0\.1)/.test(domain) ? `http://${domain}` : `https://${domain}`;
};

interface ProductPageConfigurationFormProps {
  onSubmit: (formData: PageConfiguration) => void;
  model?: Partial<PageConfiguration> | null;
  variant: CheckoutVariant;
}

const ProductPageConfigurationForm = ({ model, onSubmit, variant }: ProductPageConfigurationFormProps) => {
  const { t } = useTranslation(["products", "common"]);
  const [generalConfigForm] = Form.useForm<PageConfiguration & { showPromoVideo: boolean }>();
  const [pricingPlans, setPricingPlans] = useState<PricingPlan[]>(() => migratePricingPlans(model?.pricing));
  const [vipPricingPlans, setVipPricingPlans] = useState<PricingPlan[]>(() => migratePricingPlans(model?.vipPricing));
  const [activeTab, setActiveTab] = useState<string>("general");
  const [activeVisualSection, setActiveVisualSection] = useState<VisualSection>("general");
  const [isSaving, setIsSaving] = useState(false);
  const [isOpeningPreview, setIsOpeningPreview] = useState(false);
  const watchedGeneralConfig = Form.useWatch([], generalConfigForm) as
    | (Partial<PageConfiguration> & { showPromoVideo?: boolean })
    | undefined;

  const buildCompleteConfig = (
    generalConfigValues: Partial<PageConfiguration> & {
      showPromoVideo?: boolean;
    },
  ): PageConfiguration => {
    const { showPromoVideo, ...generalConfig } = generalConfigValues;
    // Puste FAQ znaczy „domyślne pytania szablonu”: zapis bez pytań zostawiłby stronę z pustą sekcją.
    const faqEntries = generalConfig.faq?.entries?.filter((entry) => entry?.question?.trim()) ?? [];

    return {
      ...generalConfig,
      faq: faqEntries.length ? { ...generalConfig.faq, entries: faqEntries } : undefined,
      promoVideo: showPromoVideo ? generalConfig.promoVideo : undefined,
      pricing: pricingPlans,
      vipPricing: vipPricingPlans.length > 0 ? vipPricingPlans : undefined,
    };
  };

  const draftConfig = buildCompleteConfig({
    ...model,
    ...watchedGeneralConfig,
    showPromoVideo: watchedGeneralConfig?.showPromoVideo ?? Boolean(model?.promoVideo?.videoId),
  });

  const selectVisualSection = (section: VisualSection) => {
    setActiveVisualSection(section);
    setActiveTab(section === "pricing" || section === "vipPricing" ? section : "general");
  };

  const handleOpenSecurePreview = async () => {
    setIsOpeningPreview(true);
    const previewWindow = window.open("about:blank", "_blank");

    try {
      const token = (await supabase.auth.getSession())?.data?.session?.access_token;

      if (!token) {
        throw new Error("Brak aktywnej sesji Supabase w adminie.");
      }

      const origin = getEcommerceOrigin();
      const response = await fetch(`${origin}/api/checkout-preview/create`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ slug: variant.slug }),
      });
      const payload = (await response.json().catch(() => null)) as {
        message?: string;
        url?: string;
      } | null;

      if (!response.ok || !payload?.url) {
        throw new Error(payload?.message || "Nie udało się utworzyć sesji preview.");
      }

      const previewUrl = `${origin}${payload.url}`;

      if (previewWindow) {
        previewWindow.location.href = previewUrl;
      } else {
        window.open(previewUrl, "_blank", "noopener,noreferrer");
      }
    } catch (error) {
      previewWindow?.close();
      message.error(`Nie udało się otworzyć preview: ${getErrorMessage(error)}`);
    } finally {
      setIsOpeningPreview(false);
    }
  };

  const handleSaveAll = async () => {
    setIsSaving(true);
    try {
      // Get values from the form
      const generalConfigValues = await generalConfigForm.validateFields();
      const completeConfig = buildCompleteConfig(generalConfigValues);

      onSubmit(completeConfig);
      message.success("Konfiguracja została zapisana pomyślnie");
    } catch (error) {
      message.error(`Błąd podczas zapisywania konfiguracji: ${getErrorMessage(error)}`);
    } finally {
      setIsSaving(false);
    }
  };

  const tabItems = [
    {
      key: "general",
      label: t<string>("pageConfig.generalSettings"),
      children: (
        <div className="py-4">
          <ProductPageGeneralConfigForm formController={generalConfigForm} model={model} variant={variant} />
        </div>
      ),
    },
    {
      key: "pricing",
      label: t<string>("pageConfig.pricingPlans"),
      children: (
        <div className="py-4">
          <ProductPagePricingPlansForm plans={pricingPlans} onChange={setPricingPlans} />
        </div>
      ),
    },
    {
      key: "vipPricing",
      label: t<string>("pageConfig.vipPricingPlans"),
      children: (
        <div className="py-4">
          <ProductPagePricingPlansForm plans={vipPricingPlans} onChange={setVipPricingPlans} />
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-5">
      <ProductCheckoutVariantVisualEditor
        activeSection={activeVisualSection}
        config={draftConfig}
        isOpeningPreview={isOpeningPreview}
        onOpenPreview={handleOpenSecurePreview}
        onSelectSection={selectVisualSection}
      />
      <Tabs
        activeKey={activeTab}
        onChange={(key) => {
          setActiveTab(key);
          if (key === "pricing" || key === "vipPricing") {
            setActiveVisualSection(key);
          }
        }}
        items={tabItems}
        tabBarExtraContent={
          <Button type="primary" icon={<SaveOutlined />} onClick={handleSaveAll} loading={isSaving}>
            {t<string>("common:button.save")}
          </Button>
        }
      />
    </div>
  );
};

export default ProductPageConfigurationForm;
